import { useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";

import API_URLS from "@constants/api.urls";

import DupontLogger from "@utils/DupontLogger";

import WebPubSubService from "@services/webPubSubService";

import { getRequest } from "@api/index";

const Logger = DupontLogger("useWebPubSub");

/**
 * Map of WebSocket event types to their handler functions.
 * Each handler receives (dispatch, payload) and updates Redux accordingly.
 *
 * Add new event types here as needed:
 *   EVENT_TYPE: (dispatch, payload) => { dispatch(someAction(payload)); }
 */
const eventHandlers = {
  // Example:
  // NOTIFICATION: (dispatch, payload) => {
  //   dispatch(updateGlobalData({ type: "unreadNotifications", value: payload }));
  // },
};

/**
 * Hook that listens to WebPubSub messages and dispatches Redux actions.
 * Must be used in a component that stays mounted (e.g., App.js).
 */
export const useWebPubSubListener = () => {
  const dispatch = useDispatch();
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  const handleMessage = useCallback(message => {
    Logger.info("WebPubSub received message:", message);
    const { type, payload } = message || {};
    if (!type) {
      Logger.warn("WebPubSub message missing event type:", message);
      return;
    }

    const handler = eventHandlers[type];
    if (handler) {
      handler(dispatchRef.current, payload);
    } else {
      Logger.info(`WebPubSub unhandled event type: ${type}`, payload);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = WebPubSubService.addMessageListener(handleMessage);
    return () => unsubscribe();
  }, [handleMessage]);
};

/**
 * Hook that provides connect/disconnect functions for WebPubSub.
 * Can be used in any component (e.g., SSOCallback).
 */
const useWebPubSub = () => {
  const connectWebPubSub = useCallback(async () => {
    try {
      const { data } = await getRequest(API_URLS.webPubSubscribe);
      const webSocketUrl = data?.url;
      if (webSocketUrl) {
        WebPubSubService.connect(webSocketUrl);
      } else {
        Logger.error("WebPubSub: No URL returned from subscribe API");
      }
    } catch (error) {
      Logger.error("WebPubSub subscribe error:", error);
    }
  }, []);

  const disconnectWebPubSub = useCallback(() => {
    WebPubSubService.disconnect();
  }, []);

  return { connectWebPubSub, disconnectWebPubSub };
};

export default useWebPubSub;
